import { useEffect, useRef, useState } from 'react';
import { Star, ThumbsUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Boss Cushions workshop / stock image for the "captions over photo" panel.
const HERO_IMAGE =
  'https://images.pexels.com/photos/6474493/pexels-photo-6474493.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800';

const CAPTIONS = [
  { text: 'Handcrafted right here in Melbourne', align: 'right' },
  { text: 'Premium Warwick & Wortley fabrics', align: 'left' },
  { text: 'Fast turnaround, nationwide delivery', align: 'right' },
  { text: 'Clear quotes and honest pricing', align: 'left' },
];

// Google "G" mark used as a credibility badge on each review card.
const GoogleG = ({ className = '' }) => (
  <svg viewBox="0 0 48 48" className={className} aria-label="Google">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const ReviewsCarousel = () => {
  const [data, setData] = useState({ reviews: [], rating: null, user_ratings_total: null, google_url: null });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetch(`${API}/api/google-reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!mounted) return;
        if (d && Array.isArray(d.reviews) && d.reviews.length) setData(d);
        setLoaded(true);
      })
      .catch(() => mounted && setLoaded(true));
    return () => {
      mounted = false;
    };
  }, []);

  if (loaded && data.reviews.length === 0) return null;

  // Round total down to a clean badge number (105 → "100+", 257 → "250+")
  const total = data.user_ratings_total || 0;
  const totalLabel = total >= 100 ? `${Math.floor(total / 50) * 50}+` : `${total}+`;
  const rating = (data.rating || 5).toFixed(1);

  // For each card we want to open the BUSINESS's Google Maps page (which
  // zooms straight to Boss Sofas and surfaces the reviews tab) rather than
  // the reviewer's personal profile of all the businesses they've ever reviewed.
  const businessMapsLink = data.google_url || 'https://g.page/bosssofas';
  // The 5.0 rating block links to the dedicated reviews popup for the business.
  const ratingPopupLink = data.place_id
    ? `https://search.google.com/local/reviews?placeid=${data.place_id}`
    : businessMapsLink;

  return (
    <section className="py-20 sm:py-28 bg-cream overflow-hidden" data-testid="reviews-section">
      <div className="max-w-[1664px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16 sm:mb-20">
          {/* LEFT — heading + body + Google rating block */}
          <div className="order-2 lg:order-1">
            <h2
              className="text-4xl sm:text-5xl font-display font-bold text-teal-700 mb-6 tracking-tight leading-[1.1]"
              data-testid="reviews-title"
            >
              Service that's easy to deal with, and work that's done properly.
            </h2>
            <p className="text-base sm:text-lg text-gray-600 font-body leading-relaxed mb-8 sm:mb-10">
              From the first quote to the final stitch — Boss Cushions customers love how easy,
              reliable, and professional the experience is from start to finish.
            </p>

            <a
              href={ratingPopupLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 group hover:opacity-80 transition-opacity"
              data-testid="reviews-google-rating-link"
            >
              <GoogleG className="w-7 h-7 sm:w-8 sm:h-8" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl sm:text-2xl font-bold text-[#1a1a1a] font-body">{rating}</span>
                  <span className="flex items-center gap-0.5" style={{ color: '#FBBC04' }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" strokeWidth={0} />
                    ))}
                  </span>
                </div>
                <div className="text-xs sm:text-sm text-gray-600 font-body mt-0.5 group-hover:underline">
                  Based on {totalLabel} Google Reviews & counting
                </div>
              </div>
            </a>
          </div>

          {/* RIGHT — image with floating caption pills */}
          <div className="order-1 lg:order-2">
            <CaptionedImage />
          </div>
        </div>
      </div>

      <ReviewsMarquee reviews={data.reviews} fallbackLink={businessMapsLink} />
    </section>
  );
};

/* ───────────── Image with floating caption pills (left side) ───────────── */
const CaptionedImage = () => (
  <div className="relative aspect-[4/5] sm:aspect-[3/4] max-w-[500px] mx-auto w-full">
    <img
      src={HERO_IMAGE}
      alt="Boss Cushions workshop"
      className="absolute inset-0 w-full h-full object-cover rounded-3xl shadow-lg"
      loading="lazy"
    />
    {CAPTIONS.map((c, i) => {
      // Stagger each pill vertically at 18%, 40%, 62%, 82% of the image.
      const topPct = 18 + i * 21;
      const alignClass = c.align === 'right' ? 'right-4 sm:right-[-28px]' : 'left-4 sm:left-[-28px]';
      return (
        <div
          key={i}
          className={`absolute ${alignClass} bg-white rounded-full shadow-lg pl-2.5 pr-4 py-2 sm:pl-3 sm:pr-5 sm:py-2.5 flex items-center gap-2 animate-caption-float`}
          style={{ top: `${topPct}%`, animationDelay: `${i * 0.15}s` }}
        >
          <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-teal-600 text-white flex items-center justify-center flex-shrink-0">
            <ThumbsUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
          </span>
          <span className="text-xs sm:text-sm font-body font-semibold text-[#1a1a1a] whitespace-nowrap">
            {c.text}
          </span>
        </div>
      );
    })}
  </div>
);

/* ───────────── Marquee — JS-driven auto-scroll + touch drag ───────────── */
const ReviewsMarquee = ({ reviews, fallbackLink }) => {
  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({
    offset: 0,
    loopWidth: 0,
    paused: false,
    dragging: false,
    startX: 0,
    startY: 0,
    startOffset: 0,
    dragDistance: 0,
    lastFrame: 0,
    // Velocity in pixels/sec for inertia after a flick (trackpad or drag).
    velocity: 0,
    lastPointerX: 0,
    lastPointerT: 0,
  });

  // Speed in pixels per second. Tune here.
  const SPEED = 45;
  // Multiplier applied to trackpad deltaX so a harsh swipe registers as a harsh scroll.
  const WHEEL_GAIN = 2.4;
  // Exponential velocity decay per second (lower = longer glide).
  const VELOCITY_DECAY = 2.8;
  // Below this |velocity| we treat inertia as stopped and let auto-scroll resume.
  const VELOCITY_STOP = 20;

  // Duplicate the review list once so translate(-loopWidth) lands on an
  // identical-looking start position.
  const loop = [...reviews, ...reviews];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const recalc = () => {
      // loopWidth = total width of ONE copy of the reviews array.
      stateRef.current.loopWidth = track.scrollWidth / 2;
    };
    recalc();
    window.addEventListener('resize', recalc);

    stateRef.current.lastFrame = performance.now();

    const tick = (now) => {
      const s = stateRef.current;
      const dt = Math.min((now - s.lastFrame) / 1000, 0.05); // clamp to avoid huge jumps after tab idle
      s.lastFrame = now;

      if (!s.dragging && s.loopWidth > 0) {
        let delta = 0;
        const hasInertia = Math.abs(s.velocity) > VELOCITY_STOP;
        if (hasInertia) {
          // Inertia carries the track in the direction the user flicked.
          delta = s.velocity * dt;
          // Exponential decay: v(t+dt) = v * e^(-k*dt)
          s.velocity *= Math.exp(-VELOCITY_DECAY * dt);
        } else if (!s.paused) {
          s.velocity = 0;
          delta = -SPEED * dt;
        }

        if (delta !== 0) {
          s.offset += delta;
          if (s.offset <= -s.loopWidth) s.offset += s.loopWidth;
          if (s.offset > 0) s.offset -= s.loopWidth;
          track.style.transform = `translateX(${s.offset}px)`;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    /* Trackpad / horizontal-wheel support — a two-finger left/right swipe on
     * Mac (or shift+wheel on a mouse) gives deltaX. We convert each event
     * into velocity (px/sec) so a harsh flick carries momentum and glides,
     * matching native trackpad feel. */
    const onWheel = (e) => {
      const s = stateRef.current;
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      if (absX <= absY || absX < 2) return;
      e.preventDefault();

      // Immediate scrub so the track tracks the user's fingers 1:1.
      const gained = -e.deltaX * WHEEL_GAIN;
      let next = s.offset + gained;
      if (s.loopWidth > 0) {
        while (next <= -s.loopWidth) next += s.loopWidth;
        while (next > 0) next -= s.loopWidth;
      }
      s.offset = next;
      track.style.transform = `translateX(${next}px)`;

      // Feed velocity so inertia continues after the fingers lift.
      // Typical wheel events arrive ~16ms apart, so px-per-event * 60 ≈ px/sec.
      const instantVelocity = gained * 60;
      // Blend heavily toward the latest event for responsiveness to hard flicks.
      s.velocity = s.velocity * 0.25 + instantVelocity * 0.75;
    };
    track.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', recalc);
      track.removeEventListener('wheel', onWheel);
    };
  }, [reviews.length]);

  /* Pointer handlers — unify mouse + touch for drag-to-scrub.
   * Note: we deliberately do NOT call setPointerCapture so that the native
   * `click` event still reaches the underlying <a> review card. The card's
   * own onClick handler reads stateRef.dragDistance to suppress navigation
   * when the user is scrubbing. */
  const onPointerDown = (e) => {
    const s = stateRef.current;
    s.dragging = true;
    s.startX = e.clientX;
    s.startY = e.clientY;
    s.startOffset = s.offset;
    s.dragDistance = 0;
    s.velocity = 0;
    s.lastPointerX = e.clientX;
    s.lastPointerT = performance.now();
  };
  const onPointerMove = (e) => {
    const s = stateRef.current;
    if (!s.dragging) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    s.dragDistance = Math.max(s.dragDistance, Math.hypot(dx, dy));
    let next = s.startOffset + dx;
    if (s.loopWidth > 0) {
      while (next <= -s.loopWidth) next += s.loopWidth;
      while (next > 0) next -= s.loopWidth;
    }
    s.offset = next;
    trackRef.current.style.transform = `translateX(${next}px)`;

    // Track instantaneous pointer velocity for fling-release inertia.
    const now = performance.now();
    const dt = (now - s.lastPointerT) / 1000;
    if (dt > 0) {
      const instantV = (e.clientX - s.lastPointerX) / dt;
      // Smooth slightly so a last jittery sample doesn't dominate.
      s.velocity = s.velocity * 0.4 + instantV * 0.6;
    }
    s.lastPointerX = e.clientX;
    s.lastPointerT = now;
  };
  const onPointerUp = () => {
    const s = stateRef.current;
    s.dragging = false;
    s.lastFrame = performance.now();
    // Reset dragDistance shortly after pointer-up so the *current* click can
    // still see the drag distance (and suppress navigation if needed), but
    // future taps start clean.
    setTimeout(() => { stateRef.current.dragDistance = 0; }, 50);
  };
  const onMouseEnter = () => {
    stateRef.current.paused = true;
  };
  const onMouseLeave = () => {
    stateRef.current.paused = false;
    stateRef.current.lastFrame = performance.now();
  };

  return (
    <div className="relative reviews-marquee-mask">
      <div
        ref={trackRef}
        className="flex gap-5 sm:gap-6 will-change-transform cursor-grab active:cursor-grabbing select-none touch-pan-y"
        style={{ width: 'max-content' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid="reviews-marquee"
      >
        {loop.map((rv, i) => (
          <ReviewCard
            key={`${rv.author_name || 'r'}-${i}`}
            review={rv}
            href={rv.review_url || fallbackLink}
            getDragDistance={() => stateRef.current.dragDistance}
          />
        ))}
      </div>
    </div>
  );
};

const ReviewCard = ({ review, href, getDragDistance }) => {
  const stars = Math.round(review.rating || 5);
  const onClick = (e) => {
    // Suppress navigation if this click is the tail-end of a scrub gesture.
    if (typeof getDragDistance === 'function' && getDragDistance() > 6) {
      e.preventDefault();
    }
  };
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      draggable={false}
      onClick={onClick}
      className="flex-shrink-0 w-[300px] h-[350px] bg-white rounded-2xl shadow-card p-6 sm:p-7 border border-teal-100/60 relative no-underline text-current hover:shadow-lg transition-shadow duration-300"
      data-testid="review-card"
    >
      {/* Google G badge — top-right corner */}
      <GoogleG className="absolute top-4 right-4 w-6 h-6 sm:w-7 sm:h-7 opacity-90 pointer-events-none" />

      <div className="flex items-center gap-0.5 mb-2 pr-8" style={{ color: '#FBBC04' }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
            fill={n <= stars ? 'currentColor' : 'none'}
            strokeWidth={1.5}
          />
        ))}
      </div>
      <div className="text-sm text-gray-500 mb-[0.875rem]">
        {review.relative_time_description || 'Google review'}
      </div>
      <div className="flex items-center gap-2 mb-[0.875rem]">
        {review.profile_photo_url ? (
          <img
            src={review.profile_photo_url}
            alt={review.author_name}
            className="w-7 h-7 rounded-full object-cover"
            referrerPolicy="no-referrer"
            draggable={false}
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-xs">
            {(review.author_name || '?').charAt(0)}
          </div>
        )}
        <div className="font-semibold text-sm text-[#1a1a1a]">{review.author_name}</div>
      </div>
      <p className="text-sm text-gray-700 font-body leading-relaxed line-clamp-6">
        {review.text}
      </p>
    </a>
  );
};

export default ReviewsCarousel;
